import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as BundlesService from '~/services/BundlesService';

import { Bundle, CreateBundleComponentPayload, ListBundlesQuery, UpdateBundlePayload } from '~/types/bundles/bundle';

export const bundleQueryKeys = {
  all: ['bundles'] as const,
  lists: () => [...bundleQueryKeys.all, 'list'] as const,
  list: (query: ListBundlesQuery) => [...bundleQueryKeys.lists(), query] as const,
  details: () => [...bundleQueryKeys.all, 'detail'] as const,
  detail: (bundleId: string | undefined) => [...bundleQueryKeys.details(), bundleId ?? ''] as const,
};

export function useGetBundles(query: ListBundlesQuery) {
  return useQuery({
    queryKey: bundleQueryKeys.list(query),
    queryFn: ({ signal }) => BundlesService.listBundles(query, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useGetBundle(bundleId: string | undefined) {
  return useQuery({
    queryKey: bundleQueryKeys.detail(bundleId),
    queryFn: ({ signal }) => BundlesService.getBundle(bundleId, { signal }),
    enabled: !!bundleId,
  });
}

export function useCreateBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => BundlesService.createBundle(name),
    onSuccess: (bundle) => {
      queryClient.setQueryData(bundleQueryKeys.detail(bundle.id), bundle);
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
    },
  });
}

/** Every bundle write returns the whole bundle, so the detail cache is replaced rather than refetched. */
function useBundleMutation<TVariables>(bundleId: string, mutationFn: (variables: TVariables) => Promise<Bundle>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (bundle) => {
      queryClient.setQueryData(bundleQueryKeys.detail(bundleId), bundle);
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
    },
  });
}

export function useUpdateBundle(bundleId: string) {
  return useBundleMutation(bundleId, (payload: UpdateBundlePayload) => BundlesService.updateBundle(bundleId, payload));
}

export function useCreateBundleComponent(bundleId: string) {
  return useBundleMutation(bundleId, (payload: CreateBundleComponentPayload) =>
    BundlesService.createBundleComponent(bundleId, payload),
  );
}

export function useDeleteBundleComponent(bundleId: string) {
  return useBundleMutation(bundleId, (componentId: string) =>
    BundlesService.deleteBundleComponent(bundleId, componentId),
  );
}

export function useSetBundleDepartureDates(bundleId: string) {
  return useBundleMutation(bundleId, (dates: Array<string>) => BundlesService.setBundleDepartureDates(bundleId, dates));
}
